import React, { useEffect, useState, Suspense } from "react"
import { tableCellClasses } from "@mui/material/TableCell"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Paper,
  Typography,
  FormControlLabel,
  IconButton,
  styled,
  TextField,
  Box,
  Modal,
} from "@mui/material"
import { useCustomForm } from "@/hooks/useCustomForm"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import ClearIcon from "@mui/icons-material/Clear"
import { Textarea } from "@mantine/core"
import { DevTool } from "@/components/FormDevTool"
import { toSentenceCase } from "@/utilities/helper"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { apiFetch } from "@/hooks/apiFetch"
import Loading from "@/components/Loading"
import PrimaryButton from "@/components/Buttons/PrimaryButton"
import SecondaryButton from "@/components/Buttons/SecondaryButton"
import { useAddModel } from "@/hooks/useAddModel"
import { useCrudContext } from "@/context/crudContext"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

// Define styles once and reuse them
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontWeight: "500",
    padding: "10px 20px",
    fontSize: "14px",
  },
  [`&.${tableCellClasses.body}`]: {
    color: "GrayText",
    fontWeight: "500",
    padding: "2px 20px",
    fontSize: "14px",
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.common.white,
  },
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}))

const ResourcesSchema = z.record(z.record(z.boolean()))

const RoleSchema = z.object({
  name: z.string().min(2),
  description: z.string().optional(),
  permissions: z.record(ResourcesSchema),
})

const UpdateRoleModal = () => {
  const queryClient = useQueryClient()
  const [permissions, setPermissions] = useState(null) // Set initial value to null

  const {
    api,
    errors,
    register,
    handleSubmit,
    Controller,
    control,
    isSubmitting,
    reset,
  } = useCustomForm({
    resolver: zodResolver(RoleSchema),
  })

  const { defaultOnClose, openDialog } = useAddModel()
  const { selectedRow } = useCrudContext()

  const onSubmit = async (data) => {
    const method = selectedRow ? "PUT" : "POST"
    const url = selectedRow ? `role/${selectedRow.id}` : "role"

    const response = await apiFetch(url, {
      method,
      body: JSON.stringify(data),
    })

    if (response) {
      queryClient.invalidateQueries({ queryKey: ["role"] })
      toast.success(`Role  successfully ${selectedRow ? "updated" : "added"}`)
      defaultOnClose()
      reset()
    }
  }

  const handleReset = () => {
    reset()
  }

  // Use custom query to fetch data when creating a new role
  const { data: initialPermissionsData } = useCustomQuery(
    ["permissions-all"],
    "permissions-all"
  )

  useEffect(() => {
    if (selectedRow) {
      setPermissions(selectedRow.permission_object)
    } else if (initialPermissionsData?.data) {
      setPermissions(initialPermissionsData.data)
    }
  }, [selectedRow, initialPermissionsData])

  const handleCheckboxChange = (module, resource, action, fieldOnChange) => {
    setPermissions((prev) => {
      const updatedPermissions = {
        ...prev,
        [module]: {
          ...prev[module],
          [resource]: {
            ...prev[module][resource],
            [action]: !prev[module][resource][action],
          },
        },
      }

      fieldOnChange(updatedPermissions)
      return updatedPermissions
    })
  }

  const handleFullAccessChange = (
    module,
    resource,
    allChecked,
    fieldOnChange
  ) => {
    setPermissions((prev) => {
      const updatedPermissions = {
        ...prev,
        [module]: {
          ...prev[module],
          [resource]: Object.keys(prev[module][resource]).reduce((acc, key) => {
            acc[key] = allChecked
            return acc
          }, {}),
        },
      }

      fieldOnChange(updatedPermissions)
      return updatedPermissions
    })
  }

  const handleModuleFullAccessChange = (module, allChecked, fieldOnChange) => {
    setPermissions((prev) => {
      const updatedPermissions = {
        ...prev,
        [module]: Object.keys(prev[module]).reduce((acc, resource) => {
          acc[resource] = Object.keys(prev[module][resource]).reduce(
            (accRes, action) => {
              accRes[action] = allChecked
              return accRes
            },
            {}
          )
          return acc
        }, {}),
      }

      fieldOnChange(updatedPermissions)
      return updatedPermissions
    })
  }

  const renderTable = (moduleName, resources, fieldOnChange) => {
    const actionsList = [
      ...new Set(
        Object.values(resources).flatMap((actions) => Object.keys(actions))
      ),
    ]
    const moduleAllChecked = Object.values(resources).every((resource) =>
      Object.values(resource).every(Boolean)
    )

    return (
      <TableContainer component={Paper} style={{ marginBottom: "32px" }}>
        <Typography
          variant="h6"
          style={{
            padding: "5px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {toSentenceCase(moduleName)} Permissions
          <FormControlLabel
            control={
              <Checkbox
                checked={moduleAllChecked}
                onChange={() =>
                  handleModuleFullAccessChange(
                    moduleName,
                    !moduleAllChecked,
                    fieldOnChange
                  )
                }
                color="primary"
              />
            }
            label="Full Access"
            style={{ marginLeft: "8px" }}
          />
        </Typography>
        <Table aria-label="Table">
          <TableHead className="bg-primary-50">
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "500",
                  padding: "10px 20px",
                  fontSize: "14px",
                  width: "60%",
                }}
              >
                Resource
              </TableCell>
              <TableCell align="center">All</TableCell>
              {actionsList.map((action, index) => (
                <TableCell align="center" key={index}>
                  {action}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(resources).map(([resource, actions], index) => {
              const allChecked = Object.values(actions).every(Boolean)
              return (
                <StyledTableRow key={`${moduleName}-${resource}`}>
                  <StyledTableCell>{toSentenceCase(resource)}</StyledTableCell>
                  <StyledTableCell align="center">
                    <Checkbox
                      checked={allChecked}
                      onChange={() =>
                        handleFullAccessChange(
                          moduleName,
                          resource,
                          !allChecked,
                          fieldOnChange
                        )
                      }
                    />
                  </StyledTableCell>
                  {actionsList.map((action) => (
                    <StyledTableCell align="center" key={action}>
                      {actions[action] !== undefined && (
                        <Checkbox
                          checked={actions[action]}
                          onChange={() =>
                            handleCheckboxChange(
                              moduleName,
                              resource,
                              action,
                              fieldOnChange
                            )
                          }
                        />
                      )}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <Modal open={openDialog} onClose={defaultOnClose}>
      <Box
        sx={{
          width: 1000,
          margin: "30px auto",
          bgcolor: "white",
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: 1,
          boxShadow: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: 1,
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
            }}
            className="text-black"
          >
            {selectedRow ? "Update Role " : "Add New Role "}
          </Typography>
          <IconButton aria-label="remove" size="small" onClick={defaultOnClose}>
            <ClearIcon />
          </IconButton>
        </Box>

        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                fontWeight: "500",
                fontSize: { xs: "12px", md: "16px" },
                marginBottom: 2,
              }}
              className="text-black"
            >
              Role
            </Typography>
            <TextField
              fullWidth
              label="Name"
              defaultValue={selectedRow?.name}
              sx={{
                marginBottom: 2,
                "& .MuiInputBase-input": {
                  backgroundColor: "white",
                },
              }}
              error={!!errors.name}
              helperText={errors.name?.message}
              {...register("name")}
            />

            <Textarea
              style={{ marginBottom: 2 }}
              placeholder="Description (optional)"
              error={!!errors.description}
              {...register("description")}
            />
          </Box>
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "500",
              fontSize: { xs: "12px", md: "16px" },
              marginBottom: 2,
            }}
            className="text-black"
          >
            Permissions
          </Typography>

          {permissions ? (
            <Controller
              control={control}
              name="permissions"
              defaultValue={permissions}
              render={({ field: { onChange } }) => (
                <>
                  {Object.entries(permissions).map(([moduleName, resources]) =>
                    renderTable(moduleName, resources, onChange)
                  )}
                </>
              )}
            />
          ) : (
            <>
              <Loading />
            </>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: "10px",
            }}
          >
            <SecondaryButton label="Reset" onClick={handleReset} />
            <PrimaryButton
              type="submit"
              isSubmitting={isSubmitting}
              label={selectedRow ? "Update" : "Create"}
            />
          </Box>
          <DevTool control={control} />
        </Box>
      </Box>
    </Modal>
  )
}

export default UpdateRoleModal
